import { restAPI } from "./api.ts";
import type {
  ReportingGroupCreateOneRequestType,
  ReportingGroupEstimationMethodCreateOneRequestType,
  ReportingGroupEstimationMethodDeleteOneRequestType,
  ReportingGroupEstimationMethodType,
  ReportingGroupEstimationMethodUpdateManyRequestType,
  ReportingGroupGetListRequestType,
  ReportingGroupType,
  ReportingGroupUpdateOneRequestType,
} from "../schemas/ReportingGroupSchemas.ts";
import {
  Resources,
  type ResponseErrorType,
  type ResponseSuccessType,
  fetchAndCollate,
} from "../util/index.ts";
import type {
  GetListResponseType,
  ResourceAdapterType,
  CreateOneResponseType,
  DeleteOneResponseType,
  ResourceAdapterToAdapterQueryKeysType,
  UpdateOneResponseType,
} from "../util/index.ts";

interface ReportingGroupEstimationMethodAdapterType
  extends Pick<
    ResourceAdapterType<ReportingGroupEstimationMethodType>,
    "createOne" | "deleteOne"
  > {
  createOne: (
    params: ReportingGroupEstimationMethodCreateOneRequestType
  ) => Promise<CreateOneResponseType<ReportingGroupEstimationMethodType>>;
  deleteOne: (
    params: ReportingGroupEstimationMethodDeleteOneRequestType
  ) => Promise<DeleteOneResponseType>;
  updateMany: (
    params: ReportingGroupEstimationMethodUpdateManyRequestType
  ) => Promise<boolean>;
}

export interface ReportingGroupAdapterType
  extends ResourceAdapterType<ReportingGroupType> {
  getList: (
    params: ReportingGroupGetListRequestType
  ) => Promise<GetListResponseType<ReportingGroupType>>;
  createOne: (
    params: ReportingGroupCreateOneRequestType
  ) => Promise<CreateOneResponseType<ReportingGroupType>>;
  updateOne: (
    params: ReportingGroupUpdateOneRequestType
  ) => Promise<UpdateOneResponseType<ReportingGroupType>>;
  estimationMethods: ReportingGroupEstimationMethodAdapterType;
}

/**
 * Methods to communicate with the API endpoints dealing with Reporting Groups
 */
export const ReportingGroupAdapter: ReportingGroupAdapterType = {
  /**
   * Get a list of reporting groups
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/reporting_groups/list_reporting_group
   */
  getList: async ({
    filters,
    page,
    pageSize,
    sortBy,
    sortDirection,
    ...rest
  } = {}) =>
    fetchAndCollate<ReportingGroupType>(
      ({ page, pageSize }) =>
        restAPI.nodeAPI.GET<GetListResponseType<ReportingGroupType>>({
          endpoint: "/reporting_groups",
          query: {
            page,
            page_size: pageSize,
            sort_by: sortBy,
            sort_direction: sortDirection,
            ...rest,
            ...(filters?.id ? { id: filters.id } : {}),
            ...(filters?.name ? { name: filters.name } : {}),
          },
        }),
      page,
      pageSize
    ),
  /**
   * Get a single reporting group
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/reporting_groups/get_reporting_group
   */
  getOne: ({ id }) =>
    restAPI.nodeAPI
      .GET<ReportingGroupType>({
        endpoint: `/reporting_groups/${id}`,
      })
      .then((resp) => ({
        data: resp,
      })),
  getMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  /**
   * Create a single reporting group
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/reporting_groups/create_reporting_group
   */
  createOne: (params) =>
    restAPI.nodeAPI
      .POST<ReportingGroupType>({
        endpoint: "/reporting_groups",
        body: params.data,
      })
      .then((resp) => ({ data: resp })),
  createMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  /**
   * Create a single reporting group
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/reporting_groups/update_reporting_group
   */
  updateOne: ({ id, data }) =>
    restAPI.nodeAPI
      .PUT<ReportingGroupType>({
        endpoint: `/reporting_groups/${id}`,
        body: data,
      })
      .then((resp) => ({ data: resp })),
  updateMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  /**
   * Create a single reporting group
   * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/reporting_groups/delete_reporting_group
   */
  deleteOne: ({ id }) =>
    restAPI.nodeAPI
      .DELETE<ResponseSuccessType | ResponseErrorType>({
        endpoint: `/reporting_groups/${id}`,
      })
      .then((resp) => resp),
  deleteMany: () =>
    Promise.reject({
      success: false,
      error: "Method not implemented",
    }),
  estimationMethods: {
    /**
     * Associate an estimation method to a reporting group
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/reporting_groups/add_estimation_method_reporting_group
     */
    createOne: ({ data, meta }) =>
      restAPI.nodeAPI
        .PUT<ReportingGroupEstimationMethodType>({
          endpoint: `/reporting_groups/${meta?.reportingGroupId}/estimation_methods/${data.estimationMethodId}`,
        })
        .then((resp) => ({ data: resp })),
    /**
     * Delete an estimation method relationship to a reporting group
     * @see https://staging-carbon-hub-api.s3.us-west-2.amazonaws.com/openapi/index.html#/reporting_groups/remove_estimation_method_reporting_group
     */
    deleteOne: ({ id, meta }) =>
      restAPI.nodeAPI.DELETE({
        endpoint: `/reporting_groups/${meta?.reportingGroupId}/estimation_methods/${id}`,
      }),
    /**
     * Given a list of existing reporting groups on an estimation method and a list of new reporting groups to add
     * to the method, this will:
     *  * Add the new reporting groups to the estimation method
     *  * Delete all existing reporting groups that no longer should belong to the method
     */
    updateMany: async ({
      ids: updatedReportingGroupIds,
      data: { estimationMethodId },
      meta,
    }) => {
      const { currentReportingGroups } = meta ?? {};

      const groupsToDelete = currentReportingGroups?.filter((group) => {
        return !updatedReportingGroupIds.includes(group.id);
      });

      const currentReportingGroupIds = currentReportingGroups?.map(
        (group) => group.id
      );
      const groupsToAddIds = updatedReportingGroupIds.filter((groupId) => {
        return !currentReportingGroupIds?.includes(groupId);
      });

      if (groupsToDelete && groupsToDelete?.length > 0) {
        await Promise.all(
          groupsToDelete.map((group) =>
            ReportingGroupAdapter.estimationMethods.deleteOne({
              meta: { reportingGroupId: group.id },
              id: estimationMethodId,
            })
          )
        );
      }

      if (groupsToAddIds && groupsToAddIds?.length > 0) {
        await Promise.all(
          groupsToAddIds.map((groupId) =>
            ReportingGroupAdapter.estimationMethods.createOne({
              data: { estimationMethodId: estimationMethodId },
              meta: { reportingGroupId: groupId },
            })
          )
        );
      }

      return true;
    },
  },
};

export const ReportingGroupAdapterQueryKeys: ResourceAdapterToAdapterQueryKeysType<ReportingGroupAdapterType> =
  {
    getList: (params) =>
      [Resources.REPORTING_GROUP, "getList", params] as const,
    getOne: (params) =>
      [Resources.REPORTING_GROUP, "getOne", params.id] as const,
    getMany: () => [Resources.REPORTING_GROUP] as const,
    updateMany: () => [Resources.REPORTING_GROUP] as const,
    createMany: () => [Resources.REPORTING_GROUP] as const,
    deleteMany: () => [Resources.REPORTING_GROUP] as const,
    updateOne: () => [Resources.REPORTING_GROUP] as const,
    createOne: (_) => [Resources.REPORTING_GROUP] as const,
    deleteOne: (_) => [Resources.REPORTING_GROUP] as const,
    estimationMethods: {
      createOne: () =>
        [Resources.REPORTING_GROUP, Resources.ESTIMATION_METHOD] as const,
      deleteOne: () =>
        [Resources.REPORTING_GROUP, Resources.ESTIMATION_METHOD] as const,
      updateMany: () =>
        [Resources.REPORTING_GROUP, Resources.ESTIMATION_METHOD] as const,
    },
  };
